// ------------------------------
// components/DownloadQRButton.tsx
// Self-contained component for downloading a branded QR code PNG
// ------------------------------

import React, { useRef } from "react";
import { Button, Box, Text, Stack, Center } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import * as Sentry from "@sentry/react";

// embedded base64 rizful logo (transparent png)
const rizfulLogoDataUrl =
  "data:image/png;base64,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";

interface DownloadQRButtonProps {
  lightningAddress: string;
  qrSize: number;
}

const DownloadQRButton: React.FC<DownloadQRButtonProps> = ({
  lightningAddress,
  qrSize,
}) => {
  const qrContainerRef = useRef<HTMLDivElement>(null);

  const handleDownloadQR = async () => {
    if (!qrContainerRef.current) return;

    const node = qrContainerRef.current as HTMLDivElement;

    // Clone the node so we can append a fully painted copy to the document
    const clone = node.cloneNode(true) as HTMLDivElement;
    clone.style.position = "fixed";
    clone.style.left = "12px";
    clone.style.top = "12px";
    clone.style.zIndex = "2147483647";
    clone.style.pointerEvents = "none";
    // make clone slightly wider than QR+border to avoid right-shift
    clone.style.width = `${qrSize + 80}px`;
    clone.style.height = `${qrSize + 160}px`;
    clone.style.background = "#000000";

    document.body.appendChild(clone);

    try {
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      );

      // bump pixelRatio so small text and the logo rasterize crisper
      const pixelRatio = Math.min(
        4,
        Math.max(2, Math.round((window.devicePixelRatio || 1) * 2))
      );
      const dataUrl = await toPng(clone, {
        quality: 1.0,
        pixelRatio,
        backgroundColor: "#000000",
        width: qrSize + 80,
        height: qrSize + 160,
        skipFonts: true,
      });

      const link = document.createElement("a");
      link.download = `rizful-payment-qr-${lightningAddress.split("@")[0]}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error("Error generating PNG:", error);
      Sentry.captureException(error, {
        tags: {
          component: "DownloadQRButton",
          action: "downloadPNG",
        },
        contexts: {
          qr_details: {
            lightningAddress,
            qrSize,
            payLink: `lightning:${lightningAddress}`,
            containerWidth: qrSize + 80,
            containerHeight: qrSize + 160,
            whiteBorder: whiteBorder,
            qrInnerSize: qrInnerSize,
            errorMessage: error?.message || "Unknown error",
            userAgent: navigator.userAgent,
            devicePixelRatio: window.devicePixelRatio || 1,
          },
        },
      });
    } finally {
      if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
    }
  };

  const payLink = `lightning:${lightningAddress}`;

  // white border thickness around QR
  const whiteBorder = 16;
  const qrInnerSize = Math.max(48, qrSize - whiteBorder * 2);

  return (
    <>
      <Button
        w="100%"
        variant="outline"
        leftSection={<IconDownload size={16} />}
        onClick={handleDownloadQR}
      >
        Download Payment QR
      </Button>

      <Box
        ref={qrContainerRef}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: qrSize + 80,
          height: qrSize + 160,
          minWidth: qrSize + 80,
          minHeight: qrSize + 160,
          backgroundColor: "#000000",
          padding: 16,
          fontFamily: "system-ui, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "auto",
            }}
          >
            {/* QR wrapper with white background to produce a visible border */}
            <div
              style={{
                display: "inline-block",
                background: "#ffffff",
                padding: whiteBorder,
                borderRadius: 6,
              }}
            >
              <QRCodeSVG
                value={payLink}
                size={qrInnerSize}
                bgColor="#ffffff"
                marginSize={2}
                fgColor="#000000"
                level="L"
              />
            </div>

            {/* small gap between QR and address/logo */}
            <div style={{ height: 12 }} />

            {/* Address and logo bar */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                background: "#000000",
                color: "#ffffff",
                padding: "10px 16px",
                borderRadius: 8,
                marginTop: 8,
                width: "80%",
              }}
            >
              <div
                style={{
                  color: "#ffffff",
                  fontWeight: 600,
                  marginBottom: 8,
                  fontSize: 16,
                }}
              >
                {lightningAddress}
              </div>
              <div>
                <img
                  src={rizfulLogoDataUrl}
                  alt="Rizful"
                  style={{
                    height: 28,
                    objectFit: "contain",
                    imageRendering: "auto",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </Box>
    </>
  );
};

export default DownloadQRButton;
